import { useEffect, useState, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { Navbar } from "@/components/Navbar";
import { FolderSelect } from "@/components/FolderSelect";
import { TemplatePicker } from "@/components/TemplatePicker";
import { AiGenerateModal } from "@/components/AiGenerateModal";
import { SheetTemplatePicker } from "@/components/SheetTemplatePicker";
import { ScanModal } from "@/components/ScanModal";
import { HelpButton } from "@/components/HelpButton";
import { Tour } from "@/components/Tour";

const TOUR_STEPS = [
  { title: "¡Bienvenido a Notacora!", text: "Te muestro cómo funciona cada parte de la app. Puedes saltar cuando quieras.", testid: null },
  { title: "Notas", text: "Crea notas con formato visual (negrita, listas, hojas de cálculo) y conviértelas en mapa o PDF.", testid: "new-note-button" },
  { title: "Mapas mentales", text: "Organiza tus ideas de forma visual y conéctalas entre sí.", testid: "new-mindmap-button" },
  { title: "Hojas de cálculo", text: "Cálculos y fórmulas tipo Excel, con plantillas e importar/exportar CSV.", testid: "new-sheet-button" },
  { title: "Escanear", text: "Toma una foto y la IA extrae el texto: nota, texto literal o recibo → gasto automático.", testid: "scan-button" },
  { title: "Crear con IA", text: "Describe un tema y la IA genera una nota o un mapa mental por ti (consume café).", testid: "ai-create-button" },
  { title: "Importar", text: "Trae archivos .md o .txt y se convierten en notas dentro de Notacora.", testid: "import-button" },
  { title: "Respaldo", text: "Descarga TODO tu contenido en un .zip para guardarlo como copia de seguridad.", testid: "export-zip-button" },
  { title: "Buscar", text: "Encuentra al instante cualquier nota, mapa, hoja o tarea por su texto.", testid: "search-input" },
  { title: "Calculadora y Divisas", text: "Cálculo rápido y conversión de monedas (Dólar BCV, Euro, USDT, USDC).", testid: "calculator-toggle" },
  { title: "Inicio", text: "Este botón te trae de vuelta al Panel principal desde cualquier pantalla.", testid: "navbar-home" },
  { title: "Tema claro/oscuro", text: "Cambia entre modo claro y oscuro según tu preferencia o la hora del día.", testid: "theme-toggle" },
  { title: "Notificaciones y alarmas", text: "Aquí verás avisos de tus tareas y recordatorios de la Agenda que estén por vencer.", testid: "notification-bell" },
  { title: "Tu taza de café ☕", text: "Es tu saldo mensual para funciones con IA (crear con IA, escanear). Cada uso consume un poco de café; se rellena cada mes. Toca la taza para ver cuánto te queda.", testid: "navbar-coffee" },
  { title: "Cerrar sesión", text: "Sal de tu cuenta de forma segura. Tu contenido queda guardado en la nube.", testid: "navbar-logout" },
  { title: "Menú", text: "Aquí está todo lo demás. Voy a abrirlo para mostrarte cada sección.", testid: "navbar-menu-toggle", openMenu: true },
  { title: "Mis Archivos", text: "Todo tu contenido —notas, mapas, hojas y PDFs— reunido en un solo lugar, con carpetas y filtros.", testid: "menu-mis-archivos", openMenu: true },
  { title: "Bitácora", text: "Tu registro automático de actividad: qué creaste, editaste o borraste, y cuándo. Perfecta para seguir el hilo de tu día.", testid: "menu-bitacora", openMenu: true },
  { title: "Gastos", text: "Controla ingresos y gastos en varias monedas (Bs, $, USDT, EUR), con presupuestos, ámbitos personalizados y gráficos.", testid: "menu-finanzas", openMenu: true },
  { title: "Agenda", text: "Tareas y recordatorios con fecha, prioridad y avisos que llegan a tus notificaciones.", testid: "menu-agenda", openMenu: true },
  { title: "Documentos", text: "Sube, visualiza, edita y organiza tus PDFs en carpetas.", testid: "menu-documents", openMenu: true },
  { title: "Tutoriales", text: "Videos y guías cortas de cada función, cuando las necesites.", testid: "menu-tutoriales", openMenu: true },
  { title: "Papelera", text: "Lo que borras se guarda aquí 30 días: puedes restaurarlo o eliminarlo para siempre.", testid: "menu-trash", openMenu: true },
  { title: "Ayuda en cada herramienta", text: "Toca el botón ? junto a cada título para ver su tutorial cuando quieras. ¡Listo, ya conoces Notacora!", testid: "help-getting-started" },
];
import { PAGE, BTN_PRIMARY, BTN_GHOST, INPUT, CARD, ICON_BTN } from "@/lib/styles";
import { FileText, Network, Trash2, Search, Upload, Plus, Archive, Sparkles, CalendarCheck, Flag, Table2, ScanText, Wallet, Sigma, Scale } from "lucide-react";
import { toast } from "sonner";
import { format, parseISO } from "date-fns";
import { es } from "date-fns/locale";

const PRIORITY_DOT = { high: "#f43f5e", medium: "#f59e0b", low: "#0ea5e9" };
const taskHaystack = (t) => {
  let s = (t.title || "").toLowerCase();
  if (t.due_date) {
    const d = parseISO(t.due_date.length === 10 ? t.due_date + "T00:00:00" : t.due_date);
    s += " " + [
      format(d, "dd/MM/yyyy"), format(d, "d/M/yyyy"), format(d, "yyyy-MM-dd"),
      format(d, "d 'de' MMMM yyyy", { locale: es }), format(d, "d MMMM yyyy", { locale: es }),
      format(d, "EEEE", { locale: es }), format(d, "MMMM", { locale: es }),
    ].join(" ").toLowerCase();
  }
  return s;
};

function TypeBadge({ type }) {
  const map = {
    text: { label: "Nota", Icon: FileText, cls: "text-sky-600 dark:text-sky-400 border-sky-200 dark:border-sky-500/30 bg-sky-50 dark:bg-sky-500/10" },
    mindmap: { label: "Mapa", Icon: Network, cls: "text-indigo-600 dark:text-indigo-400 border-indigo-200 dark:border-indigo-500/30 bg-indigo-50 dark:bg-indigo-500/10" },
    sheet: { label: "Hoja", Icon: Table2, cls: "text-emerald-600 dark:text-emerald-400 border-emerald-200 dark:border-emerald-500/30 bg-emerald-50 dark:bg-emerald-500/10" },
    movemap: { label: "Flujo económico", Icon: Wallet, cls: "text-orange-600 dark:text-orange-400 border-orange-200 dark:border-orange-500/30 bg-orange-50 dark:bg-orange-500/10" },
    board: { label: "Pizarra", Icon: Sigma, cls: "text-indigo-600 dark:text-indigo-400 border-indigo-200 dark:border-indigo-500/30 bg-indigo-50 dark:bg-indigo-500/10" },
    ledger: { label: "Contable", Icon: Scale, cls: "text-emerald-600 dark:text-emerald-400 border-emerald-200 dark:border-emerald-500/30 bg-emerald-50 dark:bg-emerald-500/10" },
  };
  const t = map[type] || map.text;
  const Icon = t.Icon;
  return (
    <span className={`inline-flex items-center gap-1.5 text-[10px] tracking-wider uppercase font-mono2 px-2 py-1 rounded-md border ${t.cls}`}>
      <Icon size={12} /> {t.label}
    </span>
  );
}

export default function Dashboard() {
  const navigate = useNavigate();
  const [notes, setNotes] = useState([]);
  const [q, setQ] = useState("");
  const [folder, setFolder] = useState("__all__");
  const [loading, setLoading] = useState(true);
  const [picker, setPicker] = useState(null); // null | "text" | "mindmap"
  const [aiOpen, setAiOpen] = useState(false);
  const [sheetPicker, setSheetPicker] = useState(false);
  const [scanOpen, setScanOpen] = useState(false);
  const [tourRun, setTourRun] = useState(false);

  useEffect(() => {
    if (!localStorage.getItem("notacora_tour_v1")) {
      const t = setTimeout(() => setTourRun(true), 900);
      return () => clearTimeout(t);
    }
  }, []);

  const endTour = () => { setTourRun(false); localStorage.setItem("notacora_tour_v1", "1"); };
  const [taskHits, setTaskHits] = useState([]);
  const fileRef = useRef(null);

  const load = useCallback(async (search = "", folderVal = "__all__") => {
    try {
      const params = { q: search };
      if (folderVal !== "__all__") params.folder_id = folderVal;
      const { data } = await api.get("/notes", { params });
      setNotes(data);
      const ql = search.trim().toLowerCase();
      if (ql) {
        try {
          const { data: tks } = await api.get("/tasks");
          setTaskHits(tks.filter((t) => taskHaystack(t).includes(ql)).slice(0, 8));
        } catch { setTaskHits([]); }
      } else {
        setTaskHits([]);
      }
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    const t = setTimeout(() => load(q, folder), 300);
    return () => clearTimeout(t);
  }, [q, folder, load]);

  const remove = async (e, id) => {
    e.stopPropagation();
    if (!window.confirm("¿Eliminar este elemento?")) return;
    try {
      await api.delete(`/notes/${id}`);
      setNotes((n) => n.filter((x) => x.id !== id));
      toast.success("Eliminado");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  const onImport = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    const payload = { files: [] };
    for (const f of files) {
      const content = await f.text();
      payload.files.push({ filename: f.name, content });
    }
    try {
      const { data } = await api.post("/notes/import", payload);
      toast.success(`${data.imported} archivo(s) importado(s)`);
      load(q, folder);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
    e.target.value = "";
  };

  const exportZip = async () => {
    try {
      const res = await api.get("/notes/export/zip", { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = "notacoras_backup.zip";
      a.click();
      URL.revokeObjectURL(url);
      toast.success("Respaldo descargado");
    } catch (err) {
      toast.error("No se pudo generar el respaldo");
    }
  };

  return (
    <div className={`${PAGE} font-body`}>
      <Navbar />
      <div className="max-w-6xl mx-auto p-6 md:p-10">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-5 mb-8">
          <div>
            <h1 className="font-heading font-light text-4xl sm:text-5xl tracking-tight flex items-center gap-2">Panel principal <HelpButton tool="getting-started" /><button onClick={() => setTourRun(true)} className="font-mono2 text-[11px] text-indigo-500 hover:text-indigo-400 border border-indigo-200 dark:border-indigo-500/30 rounded-full px-2.5 py-1" data-testid="restart-tour">Ver recorrido</button></h1>
            <p className="font-mono2 text-xs text-slate-500 dark:text-slate-400 mt-2">
              {notes.length} elemento(s) · sincronizados en tu cuenta
            </p>
          </div>
          <div className="flex flex-wrap gap-2.5">
            <button onClick={() => setPicker("text")} className={BTN_PRIMARY} data-testid="new-note-button">
              <Plus size={16} /> Nota
            </button>
            <button onClick={() => setPicker("mindmap")} className={BTN_GHOST} data-testid="new-mindmap-button">
              <Network size={16} /> Mapa mental
            </button>
            <button onClick={() => setSheetPicker(true)} className={BTN_GHOST} data-testid="new-sheet-button">
              <Table2 size={16} /> Hoja de cálculo
            </button>
            <button onClick={() => setScanOpen(true)} className={BTN_GHOST} data-testid="scan-button">
              <ScanText size={16} /> Escanear
            </button>
            <button onClick={() => setAiOpen(true)} className="inline-flex items-center justify-center gap-2 bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-400 hover:to-orange-400 text-white font-medium text-sm px-5 py-2.5 rounded-lg transition-all duration-300 shadow-sm shadow-amber-600/20" data-testid="ai-create-button">
              <Sparkles size={16} /> Crear con IA
            </button>
            <button onClick={() => fileRef.current?.click()} className={BTN_GHOST} data-testid="import-button">
              <Upload size={16} /> Importar
            </button>
            <button onClick={exportZip} className={BTN_GHOST} data-testid="export-zip-button">
              <Archive size={16} /> Respaldo
            </button>
            <input ref={fileRef} type="file" accept=".md,.txt,text/markdown,text/plain" multiple className="hidden" onChange={onImport} data-testid="import-input" />
          </div>
        </div>

        <div className="flex flex-wrap items-center gap-3 mb-8">
          <div className="relative flex-1 min-w-[240px] max-w-lg">
            <Search size={18} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-400" />
            <input className={`${INPUT} pl-11`} placeholder="Buscar notas, mapas o tareas (ej: 15 julio)..." value={q} onChange={(e) => setQ(e.target.value)} data-testid="search-input" />
          </div>
          <FolderSelect value={folder} onChange={setFolder} allOption allowNew={false} />
        </div>

        {taskHits.length > 0 && (
          <div className="mb-8" data-testid="task-search-results">
            <h3 className="font-heading text-sm font-medium mb-2 flex items-center gap-2 text-indigo-500">
              <CalendarCheck size={16} /> Tareas de la Agenda <span className="text-slate-400 font-normal">({taskHits.length})</span>
            </h3>
            <div className="flex flex-col gap-2">
              {taskHits.map((t) => (
                <div key={t.id} onClick={() => navigate("/agenda")} className={`${CARD} p-3 flex items-center gap-3 cursor-pointer`} data-testid={`task-hit-${t.id}`}>
                  <span className="w-2.5 h-2.5 rounded-full shrink-0" style={{ background: PRIORITY_DOT[t.priority] || PRIORITY_DOT.medium }} />
                  <span className={`font-body text-sm flex-1 truncate ${t.status === "done" ? "line-through text-slate-400" : ""}`}>{t.title}</span>
                  {t.due_date && (
                    <span className="font-mono2 text-[11px] text-slate-400 shrink-0">
                      {format(parseISO(t.due_date.length === 10 ? t.due_date + "T00:00:00" : t.due_date), "d 'de' MMMM yyyy", { locale: es })}
                    </span>
                  )}
                  <Flag size={12} className="text-slate-300 dark:text-slate-600 shrink-0" />
                </div>
              ))}
            </div>
          </div>
        )}

        {loading ? (
          <p className="font-mono2 text-sm text-slate-400">Cargando...</p>
        ) : notes.length === 0 ? (
          <div className="border border-dashed border-slate-300 dark:border-slate-700 rounded-2xl p-16 text-center" data-testid="empty-state">
            <Network size={40} className="mx-auto text-slate-300 dark:text-slate-600 mb-4" />
            <h3 className="font-heading font-normal text-xl mb-2">Aún no hay nada aquí</h3>
            <p className="font-body text-sm text-slate-500 dark:text-slate-400">Crea tu primera nota o mapa mental, o importa tus archivos .md/.txt.</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5" data-testid="notes-grid">
            {notes.map((n) => (
              <div
                key={n.id}
                className={`${CARD} p-6 cursor-pointer flex flex-col gap-4 group`}
                onClick={() => navigate(n.type === "mindmap" ? `/mindmap/${n.id}` : n.type === "sheet" ? `/hoja/${n.id}` : n.type === "movemap" ? `/movimientos/${n.id}` : n.type === "board" ? `/pizarra/${n.id}` : n.type === "ledger" ? `/contable/${n.id}` : `/note/${n.id}`)}
                data-testid={`note-card-${n.id}`}
              >
                <div className="flex items-start justify-between gap-2">
                  <TypeBadge type={n.type} />
                  <button onClick={(e) => remove(e, n.id)} className={`${ICON_BTN} opacity-0 group-hover:opacity-100`} data-testid={`delete-${n.id}`}>
                    <Trash2 size={14} />
                  </button>
                </div>
                <h3 className="font-heading font-medium text-lg leading-snug break-words">{n.title}</h3>
                <p className="font-mono2 text-[11px] text-slate-400 dark:text-slate-500 mt-auto">
                  {n.updated_at ? format(new Date(n.updated_at), "dd MMM yyyy · HH:mm") : "-"}
                </p>
              </div>
            ))}
          </div>
        )}
      </div>
      <TemplatePicker
        open={!!picker}
        type={picker}
        folderId={folder !== "__all__" ? folder : ""}
        onClose={() => setPicker(null)}
      />
      <AiGenerateModal
        open={aiOpen}
        folderId={folder !== "__all__" ? folder : ""}
        onClose={() => setAiOpen(false)}
      />
      <SheetTemplatePicker
        open={sheetPicker}
        folderId={folder !== "__all__" ? folder : ""}
        onClose={() => setSheetPicker(false)}
      />
      <ScanModal
        open={scanOpen}
        folderId={folder !== "__all__" ? folder : ""}
        onClose={() => setScanOpen(false)}
      />
      <Tour steps={TOUR_STEPS} run={tourRun} onClose={endTour} />
    </div>
  );
}
